// «Мои задачи» (030: contracts/api.md, «Мои задачи»; research Р3, Р6, Р8; ASG-DESK-01, ASG-DESK-02,
// ASG-DESK-03, ASG-DESK-05, ASG-DESK-16, ASG-ONB-06, ASG-ONB-08, ASG-ONB-10). Проекция модуля
// control по пунктам обоих режимов: строки, группы по сроку, ответы в строке, пометки изменений
// с прошлого просмотра. Право — items.own (связь учётной записи с человеком; без связи — ACC-008).
import { z } from '../kernel/zod.ts';
import { DateSchema, IdSchema, TimestampSchema } from '../kernel/common.ts';
import { ITEM_TITLE_MAX, WorkStateSchema } from '../minutes/item.ts';
import { ANSWER_NOTE_MAX, ANSWER_REASON_MAX, ReportReasonSchema } from '../minutes/answers.ts';
import { LINK_CHECK_PROPOSED_TEXT_MAX } from '../directory/link-check.ts';
import { fieldMessages } from '../kernel/field-messages.ts';

/** Отметок просмотра в одном запросе (my.seen). */
export const MY_SEEN_MAX = 200;
/** Поиск «Кто отвечает — по желанию» (my.people): строка и число записей в ответе. */
export const MY_PEOPLE_QUERY_MIN = 2;
export const MY_PEOPLE_QUERY_MAX = 100;
export const MY_PEOPLE_LIMIT = 10;

/** Группа по сроку (FR-019, FR-020): правило «Просроченных», неделя — пн–вс по поясу установки. */
export const myItemGroups = ['overdue', 'today', 'week', 'later', 'none'] as const;
export type MyItemGroup = (typeof myItemGroups)[number];
export const MyItemGroupSchema = z.enum(myItemGroups);

export const myItemGroupLabels = {
  overdue: 'Просрочено',
  today: 'Сегодня',
  week: 'На этой неделе',
  later: 'Позже',
  none: 'Без срока',
} as const satisfies Record<MyItemGroup, string>;

/**
 * Раздел внешнего (FR-042): «Мои» и «Моей компании» — только чтение. 032 (FR-026, ASG-RES-08):
 * curated — «Как куратор: <организация>», пункты организаций без адресатов, где пользователь —
 * основной куратор; только чтение.
 */
export const myItemSections = ['mine', 'company', 'curated'] as const;
export type MyItemSection = (typeof myItemSections)[number];

/**
 * Почему строку нельзя менять: file — протокол в режиме файла (Д6), company — пункт компании
 * внешнего, removed — «снят с вас» (до просмотра); 032 — curated: пункт «Как куратор» (FR-026).
 */
export const myReadOnlyReasons = ['file', 'company', 'removed', 'curated'] as const;
export type MyReadOnlyReason = (typeof myReadOnlyReasons)[number];

/**
 * Номер строки: пункт в системе — uuid; строка протокола в режиме файла — file:<версия>:<строка>
 * (отметка просмотра хранит оба вида, data-model.md `control.item_views`).
 */
export const MyItemKeySchema = z
  .string()
  .regex(
    /^(?:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|file:[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}:\d{1,6})$/,
  );

/**
 * Пометка изменения с прошлого просмотра (FR-026): «новый», «срок 10.11 → 14.11», «снят с вас»,
 * «ответил Рябов Р. Р.».
 */
export const MyMarkSchema = z.union([
  z.literal('new'),
  z.literal('removed'),
  z.object({ due: z.tuple([z.string().nullable(), z.string().nullable()]) }),
  z.object({ answeredBy: z.string() }),
]);
export type MyMark = z.infer<typeof MyMarkSchema>;

/** Строка «Моих задач» (FR-021). */
export const MyItemRowSchema = z.object({
  itemId: MyItemKeySchema,
  documentId: IdSchema,
  /** Номер пункта как в протоколе: «3.2». */
  number: z.string(),
  /** Номер протокола «П-12» и день планёрки «по средам»; дня нет — null. */
  protocol: z.object({ number: z.string(), day: z.string().nullable() }),
  /** Разделы над пунктом сверху вниз. */
  path: z.array(z.string()),
  /** «Что сделать». */
  text: z.string(),
  state: WorkStateSchema.nullable(),
  cancelled: z.boolean(),
  dueRaw: z.string().nullable(),
  /** Конец срока ISO; «Уточнить», текст и «после …» — null («Без срока»). */
  due: TimestampSchema.nullable(),
  group: MyItemGroupSchema,
  /** «Вместе с: …» — ФИО других ответственных, главный первым. */
  with: z.array(z.string()),
  changedAt: TimestampSchema,
  section: z.enum(myItemSections),
  /** 032 (FR-026): организация раздела «Как куратор: …». */
  curatedOrg: z.string().optional(),
  /** 032 (FR-029, ASG-DESK-11): «Новые для вас» — назначен после прошлого открытия экрана. */
  fresh: z.literal(true).optional(),
  readOnly: z.enum(myReadOnlyReasons).nullable(),
  /** «Открыть в протоколе»; false — «Протокол закрыт. Ваши задачи — здесь». */
  canOpen: z.boolean(),
  /**
   * «Заменить ответственного» в окне «Ошибка в пункте…» (FR-035): право правки протокола целиком
   * или зона редактора — не только «свои пункты». Нет — false.
   */
  canReassign: z.boolean().optional(),
  marks: z.array(MyMarkSchema),
  /** Версии полей для ответа (конфликт — 409, 012). */
  fieldRevs: z.object({
    workState: z.number().int().min(0),
    dueRaw: z.number().int().min(0),
  }),
});
export type MyItemRow = z.infer<typeof MyItemRowSchema>;

/** GET /api/my/items. */
export const MyItemsSchema = z.object({
  rows: z.array(MyItemRowSchema),
  counts: z.object({ open: z.number().int().min(0), done: z.number().int().min(0) }),
  /** «Изменилось с прошлого входа: N». */
  changed: z.number().int().min(0),
  /** Полоса первого входа (FR-028). */
  banner: z.boolean(),
  /** Внешний: разделы «Мои» и «Моей компании» (FR-042). */
  sections: z.boolean(),
  /** 032 (FR-023): своя отметка «В отъезде до» — последний день; нет — null. */
  away: DateSchema.nullable(),
});
export type MyItems = z.infer<typeof MyItemsSchema>;

/** GET /api/my/summary — меню и «Главная» (FR-033, FR-034). */
export const MySummarySchema = z.object({
  overdue: z.number().int().min(0),
  today: z.number().int().min(0),
  week: z.number().int().min(0),
});
export type MySummary = z.infer<typeof MySummarySchema>;

/** Пункт в адресе маршрутов «Моих задач». */
export const MyItemParamsSchema = z.strictObject({ itemId: IdSchema });
/** Команда ответа в адресе «Отменить». */
export const MyCommandParamsSchema = z.strictObject({ commandId: IdSchema });

const FieldRevSchema = z.number().int().min(0);

/** Причина переноса — 1–500 знаков (FR-022). */
export const DueReasonSchema = z
  .string({ error: fieldMessages.dueReason })
  .trim()
  .min(1, { error: fieldMessages.dueReason })
  .max(ANSWER_REASON_MAX, { error: fieldMessages.dueReason });

/**
 * POST /api/my/items/{itemId}/answer (FR-022): «Выполнено» — item.workState с фактом «сегодня»;
 * «Новый срок…» — cells.edit dueRaw и причина; «Пояснение…» — item.workState с прежним
 * состоянием. fieldRev — версия поля, которую видел человек.
 */
export const MyAnswerSchema = z.discriminatedUnion('kind', [
  z.strictObject({ commandId: IdSchema, kind: z.literal('done'), fieldRev: FieldRevSchema }),
  z.strictObject({
    commandId: IdSchema,
    kind: z.literal('due'),
    dueRaw: z.string().trim().min(1).max(ITEM_TITLE_MAX),
    reason: DueReasonSchema,
    fieldRev: FieldRevSchema,
  }),
  z.strictObject({
    commandId: IdSchema,
    kind: z.literal('note'),
    note: z.string().max(ANSWER_NOTE_MAX, { error: fieldMessages.answerNote }),
    fieldRev: FieldRevSchema,
  }),
]);
export type MyAnswer = z.input<typeof MyAnswerSchema>;

/**
 * Итог ответа и «Отменить»: ревизия протокола, строка после ответа; alreadyDoneBy — пункт уже
 * выполнен другим, команды не было («Рябов Р. Р. уже отметил выполнение»).
 */
export const MyAnswerResultSchema = z.object({
  revision: z.number().int().min(1),
  row: MyItemRowSchema,
  alreadyDoneBy: z.string().nullable(),
});
export type MyAnswerResult = z.infer<typeof MyAnswerResultSchema>;

/**
 * POST /api/my/items/{itemId}/report — «Ошибка в пункте…» (FR-035, FR-036): «Кто отвечает —
 * по желанию» записью (свой сотрудник) или текстом (внешний) — только для «Не мой пункт».
 */
export const MyReportSchema = z
  .strictObject({
    reason: ReportReasonSchema,
    note: z.string().trim().max(ANSWER_NOTE_MAX, { error: fieldMessages.answerNote }).optional(),
    proposedRecordId: IdSchema.optional(),
    proposedText: z
      .string()
      .trim()
      .min(1, { error: fieldMessages.reportText })
      .max(LINK_CHECK_PROPOSED_TEXT_MAX, { error: fieldMessages.reportText })
      .optional(),
  })
  .refine(
    (body) =>
      body.reason === 'not_mine' ||
      (body.proposedRecordId === undefined && body.proposedText === undefined),
    { message: '«Кто отвечает» — только для «Не мой пункт»', path: ['proposedRecordId'] },
  )
  .refine((body) => body.proposedRecordId === undefined || body.proposedText === undefined, {
    message: 'Запись или текст — что-то одно',
    path: ['proposedText'],
  });
export type MyReport = z.input<typeof MyReportSchema>;

/** POST /api/my/seen — строки были на экране (FR-026). */
export const MySeenBodySchema = z.strictObject({
  itemIds: z.array(MyItemKeySchema).min(1).max(MY_SEEN_MAX),
});
export type MySeenBody = z.input<typeof MySeenBodySchema>;

/** GET /api/my/items/{itemId}/section — пункты раздела списком только для чтения (FR-031). */
export const MySectionSchema = z.object({
  protocol: z.object({ number: z.string(), day: z.string().nullable() }),
  path: z.array(z.string()),
  rows: z.array(
    z.object({
      itemId: IdSchema,
      number: z.string(),
      /** Глубина под разделом: 0 — пункт раздела. */
      depth: z.number().int().min(0),
      text: z.string(),
      state: WorkStateSchema.nullable(),
      dueRaw: z.string().nullable(),
      assignees: z.array(z.string()),
      /** Пункт человека — выделен. */
      mine: z.boolean(),
    }),
  ),
});
export type MySection = z.infer<typeof MySectionSchema>;

/** GET /api/my/people?q= — «Кто отвечает — по желанию» (своим; внешнему — ACC-001). */
export const MyPeopleQuerySchema = z.strictObject({
  q: z.string().trim().min(MY_PEOPLE_QUERY_MIN).max(MY_PEOPLE_QUERY_MAX),
});
export type MyPeopleQuery = z.input<typeof MyPeopleQuerySchema>;

export const MyPeopleResponseSchema = z.object({
  items: z
    .array(z.object({ id: IdSchema, name: z.string(), unit: z.string().nullable() }))
    .max(MY_PEOPLE_LIMIT),
});
export type MyPeopleResponse = z.infer<typeof MyPeopleResponseSchema>;
